import { useState } from "react";
import { X, Download, Copy, Check, Share2 } from "lucide-react";
import { toast } from "sonner";

/**
 * ShareTruth: Branded social card preview + share/download.
 * Renders a button-only API: caller passes `truth_id` and an optional className.
 *
 * Backend: GET /api/truths/{truth_id}/share.png  → 1080×1080 PNG
 */
export default function ShareTruth({ truth_id, label = "Share My Truth", className = "" }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const base = process.env.REACT_APP_BACKEND_URL || "";
  const imgUrl = `${base}/api/truths/${truth_id}/share.png`;
  const truthUrl = `${window.location.origin}/truths/${truth_id}`;

  const download = async () => {
    setDownloading(true);
    try {
      const res = await fetch(imgUrl);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${truth_id}-truth.png`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success("Saved! Now post it on Instagram, Facebook, anywhere.");
    } catch {
      toast.error("Download failed");
    } finally {
      setDownloading(false);
    }
  };

  const nativeShare = async () => {
    try {
      const res = await fetch(imgUrl);
      const blob = await res.blob();
      const file = new File([blob], `${truth_id}-truth.png`, { type: "image/png" });
      const payload = {
        title: `My Truth · ${truth_id}`,
        text: `My truth has found its home. ${truthUrl}`,
        files: [file],
      };
      if (navigator.canShare?.(payload)) {
        await navigator.share(payload);
        return;
      }
      // Fallback to URL share if files unsupported
      if (navigator.share) {
        await navigator.share({ title: payload.title, text: payload.text, url: truthUrl });
        return;
      }
      // Last resort: download
      download();
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Couldn't open share sheet — try Download instead.");
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(truthUrl);
      setCopied(true); setTimeout(() => setCopied(false), 2000);
      toast.success("Link copied");
    } catch {}
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid={`share-truth-btn-${truth_id}`}
        className={
          className ||
          "inline-flex items-center gap-2 px-6 py-3 border border-[#C9A055] text-[#C9A055] hover:bg-[#C9A055] hover:text-black font-ui text-[10px] tracking-[0.32em] uppercase transition-colors"
        }
      >
        <Share2 size={13}/> {label}
      </button>

      {open && (
        <div className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div
            className="bg-[#050505] border border-[#C9A055]/30 w-full max-w-md max-h-[92vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="share-truth-modal"
          >
            <div className="flex justify-between items-center px-5 py-4 border-b border-white/10">
              <div>
                <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055]">SHARE MY TRUTH</div>
                <div className="font-display text-xl text-[#F4F1EA]">{truth_id}</div>
              </div>
              <button onClick={() => setOpen(false)} className="text-[#8a857d] hover:text-[#F4F1EA]" data-testid="share-modal-close"><X size={20}/></button>
            </div>

            <div className="p-5 space-y-4">
              <div className="aspect-square border border-white/10 bg-black overflow-hidden">
                <img
                  src={imgUrl}
                  alt={`Share card for ${truth_id}`}
                  className="w-full h-full object-cover"
                  data-testid="share-truth-preview"
                  loading="lazy"
                />
              </div>

              <p className="font-body text-[12px] text-[#8a857d] leading-relaxed">
                A 1080×1080 social card with your art, your city, and your Truth ID. Perfect for Instagram, Facebook, or anywhere you want to celebrate your truth finding its home.
              </p>

              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={download}
                  disabled={downloading}
                  data-testid="share-download-btn"
                  className="inline-flex items-center justify-center gap-2 px-4 py-3 bg-[#C9A055] text-black hover:opacity-90 font-ui text-[10px] tracking-[0.32em] uppercase disabled:opacity-40"
                >
                  <Download size={13}/> {downloading ? "Saving…" : "Download"}
                </button>
                <button
                  onClick={nativeShare}
                  data-testid="share-native-btn"
                  className="inline-flex items-center justify-center gap-2 px-4 py-3 border border-[#C9A055] text-[#C9A055] hover:bg-[#C9A055] hover:text-black font-ui text-[10px] tracking-[0.32em] uppercase transition-colors"
                >
                  <Share2 size={13}/> Share
                </button>
              </div>

              <button
                onClick={copyLink}
                data-testid="share-copy-link"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-white/15 text-[#9a9690] hover:border-white/30 hover:text-[#F4F1EA] font-ui text-[10px] tracking-[0.22em] uppercase transition-colors"
              >
                {copied ? <><Check size={13}/> Copied!</> : <><Copy size={13}/> Copy Truth Page Link</>}
              </button>

              <div className="border border-white/5 bg-[#0a0a0a] p-3">
                <div className="font-ui text-[9px] tracking-[0.32em] text-[#8a857d]">DIRECT LINK</div>
                <div className="font-body text-[11px] text-[#C9A055] break-all mt-1">{truthUrl}</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
